/**
 * adaptiveLearning.ts — MER Algo Footprints v8.5.1
 *
 * Deterministic Adaptive Learning Layer.
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — same inputs, same outputs. No Date.now() in semantic output.
 * 2. Conservative — modifiers bounded to [MIN_MODIFIER, MAX_MODIFIER] = [0.85, 1.15]
 * 3. Advisory mode by default — active modifiers require explicit feature flag
 * 4. Explainable — every recommendation carries a named rule + reason string
 * 5. Regime-conditioned — learning segments by STRUCTURED RegimeKey, not text parsing
 * 6. Per-signal-family calibration profiles (gamma_breakout, trap_warning, etc.)
 *
 * ── Allowed adaptive targets ──────────────────────────────────────────────────
 *   ✓ Confidence scaling recommendations
 *   ✓ Attribution layer weighting
 *   ✓ Signal ranking / prioritization
 *   ✓ Warning severity suggestions
 *   ✓ Lesson / recommendation generation
 *
 * ── Forbidden adaptive targets (NEVER touch these) ────────────────────────────
 *   ✗ Gamma contribution formulas
 *   ✗ Wall / flip-zone calculations
 *   ✗ Raw liquidity scoring
 *   ✗ Volatility regime math
 *   ✗ Trap classification rules
 *
 * ── Adaptation modes ─────────────────────────────────────────────────────────
 *   shadow   — learn only, no output changes, no visible recommendations
 *   advisory — bounded recommendations surfaced to operator (default)
 *   active   — recommendations applied via applyAdaptiveModifiers() when
 *              ENABLE_ACTIVE_ADAPTATION config flag is true
 */

import type {
  OutcomeValidationResult,
  PrimaryAttributionLayer,
  CalibrationResult,
} from '../replay/outcomeValidation';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const ADAPTIVE_LEARNING_THRESHOLDS = {
  // Minimum samples before any adaptation fires
  MIN_SAMPLE_SIZE:              5,
  // Minimum samples per regime bucket for regime-conditioned learning
  MIN_REGIME_SAMPLE_SIZE:       3,
  // Minimum samples per signal family for per-signal calibration
  MIN_SIGNAL_SAMPLE_SIZE:       3,
  // Recency window — how many recent samples to weight more heavily
  RECENCY_WINDOW:               10,
  // Recency weight multiplier
  RECENCY_WEIGHT:               2.0,

  // Modifier bounds — all learned adjustments clamped to this range
  MIN_MODIFIER:                 0.85,
  MAX_MODIFIER:                 1.15,
  NEUTRAL_MODIFIER:             1.00,

  // Accuracy thresholds
  HIGH_RELIABILITY_MIN:         0.65,
  LOW_RELIABILITY_MAX:          0.45,
  WEIGHT_BOOST_ACCURACY_MIN:    0.65,
  WEIGHT_REDUCE_ACCURACY_MAX:   0.40,

  // Calibration bias thresholds
  OVERCONFIDENCE_RATE_MAX:      0.40,
  UNDERCONFIDENCE_RATE_MIN:     0.40,
  CONFIDENCE_ADJUST_STEP:       0.05,

  // Lesson extraction
  LESSON_MIN_PATTERN_COUNT:     2,
} as const;

const T = ADAPTIVE_LEARNING_THRESHOLDS;

// ── Structured regime key ─────────────────────────────────────────────────────
//
// Replaces text-parsing regime extraction entirely.
// All regime conditioning is driven from structured snapshot fields.

export interface RegimeKey {
  volatility: string;    // e.g. 'normal_vol', 'high_vol', 'extreme_vol', 'low_vol', 'unknown'
  gamma:      string;    // e.g. 'Short Gamma / Expansion', 'Long Gamma / Pinning', 'unknown'
  thesis:     string;    // e.g. 'breakout', 'meanReversion', 'neutral', 'unknown'
  trap:       string | null;  // e.g. 'bull_trap', 'bear_trap', null = no trap
}

/** Compact string key for grouping — deterministic from RegimeKey fields */
export function regimeKeyString(rk: RegimeKey): string {
  return [
    rk.volatility || 'unknown',
    rk.gamma      || 'unknown',
    rk.thesis     || 'unknown',
    rk.trap       ?? 'no_trap',
  ].join('|');
}

// ── Signal family keys ────────────────────────────────────────────────────────
//
// Derived from attribution + regime context to enable per-signal calibration.

export type SignalFamilyKey =
  | 'gamma_breakout'
  | 'gamma_pinning'
  | 'trap_warning'
  | 'structure_reversal'
  | 'volatility_instability'
  | 'thesis_high_conviction'
  | 'mixed_convergence'
  | 'other';

export function deriveSignalFamily(e: OutcomeValidationResult): SignalFamilyKey {
  const driver = e.attribution.primaryDriver;
  const conv   = e.attribution.convergence;

  if (conv === 'multi_driver') return 'mixed_convergence';
  if (driver === 'gamma') {
    return e.reasoning.some(r => r.toLowerCase().includes('pin') || r.toLowerCase().includes('long gamma'))
      ? 'gamma_pinning' : 'gamma_breakout';
  }
  if (driver === 'trap')       return 'trap_warning';
  if (driver === 'structure')  return 'structure_reversal';
  if (driver === 'volatility') return 'volatility_instability';
  if (driver === 'thesis' && e.conviction >= 70) return 'thesis_high_conviction';
  return 'other';
}

// ── Core types ────────────────────────────────────────────────────────────────

export type AdaptationMode = 'shadow' | 'advisory' | 'active';
export type ReliabilityTier = 'high' | 'moderate' | 'low' | 'insufficient_data';

export interface SignalReliabilityStats {
  signalKey:           PrimaryAttributionLayer | string;
  sampleSize:          number;
  validatedCount:      number;
  failedCount:         number;
  partialCount:        number;
  accuracyRate:        number | null;
  recentAccuracyRate:  number | null;
  avgConviction:       number | null;
  calibrationBias:     CalibrationResult;
  reliabilityTier:     ReliabilityTier;
}

export interface SignalCalibrationProfile {
  signalKey:           SignalFamilyKey | 'global';
  sampleSize:          number;
  overconfidentCount:  number;
  underconfidentCount: number;
  wellCalibratedCount: number;
  confidenceBias:      'overconfident' | 'underconfident' | 'neutral' | 'insufficient_data';
  /** Multiplier: <1 = reduce confidence, >1 = boost confidence. null = no recommendation */
  suggestedAdjustment: number | null;
  explanation:         string;
}

export interface AdaptiveWeightProfile {
  layer:           PrimaryAttributionLayer;
  sampleSize:      number;
  baseWeight:      number;
  learnedModifier: number;
  effectiveWeight: number;
  reason:          string;
  appliedRule:     string;
}

export interface RegimeConditionedStats {
  regimeKey:     RegimeKey;
  regimeKeyStr:  string;   // compact string key for stable serialisation
  stats:         SignalReliabilityStats;
}

/** Full audit entry for every applied modifier — no hidden adjustments */
export interface AdaptiveAuditEntry {
  signalKey:      string;
  baseValue:      number;
  modifier:       number;
  adjustedValue:  number;
  reason:         string;
  regimeContext:  RegimeKey;
  sampleSize:     number;
  appliedRule:    string;
}

export interface AdaptiveLearningProfile {
  symbol:               string;
  evaluationCount:      number;
  mode:                 AdaptationMode;
  reliability:          SignalReliabilityStats[];
  calibrationProfiles:  SignalCalibrationProfile[];
  weightProfiles:       AdaptiveWeightProfile[];
  regimeStats:          RegimeConditionedStats[];
  lessons:              string[];
  reasoning:            string[];
  meta: {
    sufficientData:     boolean;
    computedFromCount:  number;
  };
}

// ── Step 1: Reliability stats ─────────────────────────────────────────────────

function _buildReliabilityStats(
  evaluations: OutcomeValidationResult[],
  layerKey:    PrimaryAttributionLayer | string,
): SignalReliabilityStats {
  const relevant = evaluations.filter(e =>
    e.attribution.primaryDriver === layerKey &&
    e.decisionStatus !== 'passed' && e.decisionStatus !== 'pending'
  );

  if (relevant.length === 0) {
    return {
      signalKey: layerKey, sampleSize: 0, validatedCount: 0, failedCount: 0, partialCount: 0,
      accuracyRate: null, recentAccuracyRate: null, avgConviction: null,
      calibrationBias: 'insufficient_data', reliabilityTier: 'insufficient_data',
    };
  }

  const wins    = relevant.filter(e => e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'weak_win').length;
  const losses  = relevant.filter(e => e.outcomeQuality === 'weak_loss'  || e.outcomeQuality === 'strong_loss').length;
  const actedOn = wins + losses;
  const accuracy = actedOn > 0 ? wins / actedOn : null;

  // Recency-weighted accuracy
  const sorted  = [...relevant].sort((a, b) => a.snapshotTs - b.snapshotTs);
  const recentN = Math.min(T.RECENCY_WINDOW, sorted.length);
  const recent  = sorted.slice(-recentN);
  let rWeightedWins = 0, rWeightedTotal = 0;
  recent.forEach((e, i) => {
    const w    = i >= recentN / 2 ? T.RECENCY_WEIGHT : 1.0;
    const isW  = e.outcomeQuality === 'strong_win'  || e.outcomeQuality === 'weak_win';
    const isL  = e.outcomeQuality === 'weak_loss'   || e.outcomeQuality === 'strong_loss';
    if (isW || isL) { rWeightedTotal += w; if (isW) rWeightedWins += w; }
  });
  const recentAccuracy = rWeightedTotal > 0 ? rWeightedWins / rWeightedTotal : null;

  const avgConviction = Math.round(relevant.reduce((s, e) => s + e.conviction, 0) / relevant.length);

  const calibCounts: Record<string, number> = {};
  for (const e of relevant) calibCounts[e.calibration.calibrationResult] = (calibCounts[e.calibration.calibrationResult] ?? 0) + 1;
  const calibBias = (Object.entries(calibCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'insufficient_data') as CalibrationResult;

  const reliabilityTier: ReliabilityTier =
    relevant.length < T.MIN_SAMPLE_SIZE    ? 'insufficient_data'
    : accuracy === null                    ? 'insufficient_data'
    : accuracy >= T.HIGH_RELIABILITY_MIN   ? 'high'
    : accuracy <= T.LOW_RELIABILITY_MAX    ? 'low'
    : 'moderate';

  return {
    signalKey: layerKey, sampleSize: relevant.length,
    validatedCount: wins, failedCount: losses, partialCount: relevant.length - wins - losses,
    accuracyRate:      accuracy      !== null ? Math.round(accuracy      * 100) / 100 : null,
    recentAccuracyRate: recentAccuracy !== null ? Math.round(recentAccuracy * 100) / 100 : null,
    avgConviction, calibrationBias: calibBias, reliabilityTier,
  };
}

// ── Step 2: Per-signal calibration profiles ───────────────────────────────────

function _buildSignalCalibrationProfile(
  evaluations: OutcomeValidationResult[],
  signalKey:   SignalFamilyKey | 'global',
): SignalCalibrationProfile {
  // For 'global': use all evaluations. For specific keys: filter by signal family.
  const relevant = signalKey === 'global'
    ? evaluations.filter(e => e.calibration.calibrationResult !== 'insufficient_data')
    : evaluations.filter(e =>
        deriveSignalFamily(e) === signalKey &&
        e.calibration.calibrationResult !== 'insufficient_data'
      );

  const n = relevant.length;

  if (n === 0) {
    return {
      signalKey, sampleSize: 0,
      overconfidentCount: 0, underconfidentCount: 0, wellCalibratedCount: 0,
      confidenceBias: 'insufficient_data', suggestedAdjustment: null,
      explanation: 'No calibration data',
    };
  }

  const overconfident  = relevant.filter(e => e.calibration.calibrationResult === 'overconfident').length;
  const underconfident = relevant.filter(e => e.calibration.calibrationResult === 'underconfident').length;
  const wellCalib      = n - overconfident - underconfident;

  const overRate  = overconfident  / n;
  const underRate = underconfident / n;

  const minSample = signalKey === 'global' ? T.MIN_SAMPLE_SIZE : T.MIN_SIGNAL_SAMPLE_SIZE;

  let bias: SignalCalibrationProfile['confidenceBias'] = 'neutral';
  let suggestedAdjustment: number | null = null;
  let explanation: string;

  if (n < minSample) {
    bias = 'insufficient_data';
    explanation = `Insufficient data (${n} < ${minSample} required)`;
  } else if (overRate > T.OVERCONFIDENCE_RATE_MAX) {
    bias = 'overconfident';
    const severity   = Math.min(2, Math.round((overRate - T.OVERCONFIDENCE_RATE_MAX) / 0.15));
    suggestedAdjustment = Math.max(T.MIN_MODIFIER, 1.0 - (severity + 1) * T.CONFIDENCE_ADJUST_STEP);
    explanation = `${(overRate * 100).toFixed(0)}% overconfident — suggest ×${suggestedAdjustment.toFixed(2)} confidence`;
  } else if (underRate > T.UNDERCONFIDENCE_RATE_MIN) {
    bias = 'underconfident';
    const severity   = Math.min(2, Math.round((underRate - T.UNDERCONFIDENCE_RATE_MIN) / 0.15));
    suggestedAdjustment = Math.min(T.MAX_MODIFIER, 1.0 + (severity + 1) * T.CONFIDENCE_ADJUST_STEP);
    explanation = `${(underRate * 100).toFixed(0)}% underconfident — suggest ×${suggestedAdjustment.toFixed(2)} confidence`;
  } else {
    explanation = `Well calibrated (${(wellCalib / n * 100).toFixed(0)}% of ${n} samples)`;
  }

  return {
    signalKey, sampleSize: n,
    overconfidentCount: overconfident, underconfidentCount: underconfident, wellCalibratedCount: wellCalib,
    confidenceBias: bias, suggestedAdjustment, explanation,
  };
}

// ── Step 3: Weight profiles ───────────────────────────────────────────────────

function _buildWeightProfile(stats: SignalReliabilityStats): AdaptiveWeightProfile {
  const layer    = stats.signalKey as PrimaryAttributionLayer;
  const accuracy = stats.recentAccuracyRate ?? stats.accuracyRate;

  if (stats.sampleSize < T.MIN_SAMPLE_SIZE || accuracy === null) {
    return {
      layer, sampleSize: stats.sampleSize, baseWeight: 1.0,
      learnedModifier: T.NEUTRAL_MODIFIER, effectiveWeight: T.NEUTRAL_MODIFIER,
      reason: 'Insufficient data', appliedRule: 'RULE_WEIGHT_INSUFFICIENT_DATA',
    };
  }

  let modifier: number = T.NEUTRAL_MODIFIER;
  let rule: string;
  let reason: string;

  if (accuracy >= T.WEIGHT_BOOST_ACCURACY_MIN) {
    modifier = Math.min(T.MAX_MODIFIER, 1.0 + (accuracy - T.WEIGHT_BOOST_ACCURACY_MIN) * 0.5);
    modifier = Math.round(modifier * 100) / 100;
    rule = 'RULE_WEIGHT_BOOST_HIGH_ACCURACY';
    reason = `${(accuracy * 100).toFixed(0)}% accuracy (${stats.sampleSize} samples) — ${layer} layer reliable`;
  } else if (accuracy <= T.WEIGHT_REDUCE_ACCURACY_MAX) {
    modifier = Math.max(T.MIN_MODIFIER, 1.0 - (T.WEIGHT_REDUCE_ACCURACY_MAX - accuracy) * 0.5);
    modifier = Math.round(modifier * 100) / 100;
    rule = 'RULE_WEIGHT_REDUCE_LOW_ACCURACY';
    reason = `${(accuracy * 100).toFixed(0)}% accuracy (${stats.sampleSize} samples) — ${layer} layer underperforming`;
  } else {
    rule = 'RULE_WEIGHT_NEUTRAL';
    reason = `${(accuracy * 100).toFixed(0)}% accuracy — ${layer} near baseline`;
  }

  const clamped = Math.max(T.MIN_MODIFIER, Math.min(T.MAX_MODIFIER, modifier));
  return {
    layer, sampleSize: stats.sampleSize, baseWeight: 1.0,
    learnedModifier: clamped, effectiveWeight: clamped, reason, appliedRule: rule,
  };
}

// ── Step 4: Structured regime conditioning ────────────────────────────────────
//
// Uses RegimeKey from evaluation context — NOT text parsing.
// OutcomeValidationResult.reasoning contains structured regime in the last line.
// Format: "Regime: <regimeDominant> | Gamma: <gammaRegime> | Traps: <n>"
// We parse this ONCE here in a controlled, validated way.

function _extractRegimeKey(e: OutcomeValidationResult): RegimeKey | null {
  const line = e.reasoning.find(r => r.startsWith('Regime:'));
  if (!line) return null;

  // "Regime: breakout | Gamma: Short Gamma / Expansion | Traps: 2"
  const regimeMatch = line.match(/^Regime:\s*([^|]+)/);
  const gammaMatch  = line.match(/\|\s*Gamma:\s*([^|]+)/);
  return {
    thesis:     regimeMatch ? regimeMatch[1].trim().toLowerCase() : 'unknown',
    gamma:      gammaMatch  ? gammaMatch[1].trim()                : 'unknown',
    volatility: 'unknown',  // volatility not yet in ResearchSnapshot evidence; placeholder
    trap:       (e.attribution.weights.some(w => w.layer === 'trap')) ? 'active' : null,
  };
}

function _buildRegimeStats(evaluations: OutcomeValidationResult[]): RegimeConditionedStats[] {
  const groups = new Map<string, { rk: RegimeKey; evals: OutcomeValidationResult[] }>();

  for (const e of evaluations) {
    const rk = _extractRegimeKey(e);
    if (!rk) continue;
    const key = regimeKeyString(rk);
    if (!groups.has(key)) groups.set(key, { rk, evals: [] });
    groups.get(key)!.evals.push(e);
  }

  const results: RegimeConditionedStats[] = [];
  for (const [keyStr, { rk, evals }] of groups.entries()) {
    if (evals.length < T.MIN_REGIME_SAMPLE_SIZE) continue;

    const wins    = evals.filter(e => e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'weak_win').length;
    const losses  = evals.filter(e => e.outcomeQuality === 'weak_loss'  || e.outcomeQuality === 'strong_loss').length;
    const actedOn = wins + losses;
    const accuracy = actedOn > 0 ? wins / actedOn : null;
    const reliabilityTier: ReliabilityTier = accuracy === null ? 'insufficient_data'
      : accuracy >= T.HIGH_RELIABILITY_MIN ? 'high'
      : accuracy <= T.LOW_RELIABILITY_MAX  ? 'low' : 'moderate';

    results.push({
      regimeKey: rk,
      regimeKeyStr: keyStr,
      stats: {
        signalKey: keyStr, sampleSize: evals.length,
        validatedCount: wins, failedCount: losses, partialCount: evals.length - wins - losses,
        accuracyRate: accuracy !== null ? Math.round(accuracy * 100) / 100 : null,
        recentAccuracyRate: null, avgConviction: null,
        calibrationBias: 'insufficient_data', reliabilityTier,
      },
    });
  }

  return results.sort((a, b) => b.stats.sampleSize - a.stats.sampleSize);
}

// ── Step 5: Lesson extraction ─────────────────────────────────────────────────

function _extractLessons(
  reliability:  SignalReliabilityStats[],
  calibration:  SignalCalibrationProfile[],
  weightProfs:  AdaptiveWeightProfile[],
  regimeStats:  RegimeConditionedStats[],
): string[] {
  const lessons: string[] = [];

  const highLayers = reliability.filter(s => s.reliabilityTier === 'high' && s.sampleSize >= T.MIN_SAMPLE_SIZE);
  const lowLayers  = reliability.filter(s => s.reliabilityTier === 'low'  && s.sampleSize >= T.MIN_SAMPLE_SIZE);

  if (highLayers.length > 0)
    lessons.push(`High-reliability: ${highLayers.map(s => `${s.signalKey}(${(s.accuracyRate! * 100).toFixed(0)}%)`).join(', ')} — prioritize when primary driver`);
  if (lowLayers.length > 0)
    lessons.push(`Low-reliability: ${lowLayers.map(s => `${s.signalKey}(${(s.accuracyRate! * 100).toFixed(0)}%)`).join(', ')} — await confirmation before acting`);

  for (const c of calibration) {
    if (c.confidenceBias === 'overconfident' && c.sampleSize >= T.MIN_SIGNAL_SAMPLE_SIZE)
      lessons.push(`${c.signalKey === 'global' ? 'Overall' : c.signalKey}: overconfidence bias — temper conviction before committing`);
    if (c.confidenceBias === 'underconfident' && c.sampleSize >= T.MIN_SIGNAL_SAMPLE_SIZE)
      lessons.push(`${c.signalKey === 'global' ? 'Overall' : c.signalKey}: underconfidence bias — trust structural signals more`);
  }

  const goodRegimes = regimeStats.filter(r => r.stats.reliabilityTier === 'high');
  const poorRegimes = regimeStats.filter(r => r.stats.reliabilityTier === 'low');
  if (goodRegimes.length > 0)
    lessons.push(`Best regime performance: ${goodRegimes.map(r => r.stats.signalKey).join(', ')}`);
  if (poorRegimes.length > 0)
    lessons.push(`Reduced edge in: ${poorRegimes.map(r => r.stats.signalKey).join(', ')} — apply tighter criteria`);

  const boostLayers  = weightProfs.filter(w => w.learnedModifier > T.NEUTRAL_MODIFIER && w.sampleSize >= T.MIN_SAMPLE_SIZE);
  const reduceLayers = weightProfs.filter(w => w.learnedModifier < T.NEUTRAL_MODIFIER && w.sampleSize >= T.MIN_SAMPLE_SIZE);
  if (boostLayers.length > 0)
    lessons.push(`Increased trust suggested: ${boostLayers.map(w => `${w.layer}(×${w.learnedModifier})`).join(', ')}`);
  if (reduceLayers.length > 0)
    lessons.push(`Reduced reliance suggested: ${reduceLayers.map(w => `${w.layer}(×${w.learnedModifier})`).join(', ')}`);

  if (lessons.length === 0)
    lessons.push('Insufficient validated outcomes for adaptive lessons. Mark snapshots as worked/failed to build the profile.');

  return lessons;
}

// ── Main export ───────────────────────────────────────────────────────────────

const ALL_LAYERS: PrimaryAttributionLayer[] = ['gamma', 'liquidity', 'trap', 'volatility', 'structure', 'thesis'];
const SIGNAL_FAMILIES: (SignalFamilyKey | 'global')[] = [
  'global', 'gamma_breakout', 'gamma_pinning', 'trap_warning',
  'structure_reversal', 'volatility_instability', 'thesis_high_conviction', 'mixed_convergence',
];

/**
 * Build a deterministic Adaptive Learning Profile.
 *
 * PURE FUNCTION — no Date.now(), no I/O, no side effects.
 * Shadow mode zeros all recommendations. Advisory mode produces bounded recommendations.
 * Active mode is identical to advisory — modifiers are applied separately via applyAdaptiveModifiers().
 */
export function buildAdaptiveLearningProfile(
  symbol:      string,
  evaluations: OutcomeValidationResult[],
  mode:        AdaptationMode = 'advisory',
): AdaptiveLearningProfile {
  const sufficientData = evaluations.length >= T.MIN_SAMPLE_SIZE;

  const reliability        = ALL_LAYERS.map(l => _buildReliabilityStats(evaluations, l)).filter(s => s.sampleSize > 0);
  const rawCalibProfiles   = SIGNAL_FAMILIES.map(k => _buildSignalCalibrationProfile(evaluations, k)).filter(c => c.sampleSize > 0);
  const rawWeightProfiles  = reliability.filter(s => s.sampleSize >= T.MIN_SAMPLE_SIZE).map(_buildWeightProfile);
  const regimeStats        = _buildRegimeStats(evaluations);
  const lessons            = _extractLessons(reliability, rawCalibProfiles, rawWeightProfiles, regimeStats);

  const reasoning: string[] = [
    `Mode: ${mode} | Symbol: ${symbol} | Evaluations: ${evaluations.length}`,
    `Sufficient data: ${sufficientData} (requires ≥${T.MIN_SAMPLE_SIZE})`,
    `Active layers: ${reliability.map(r => r.signalKey).join(', ') || 'none'}`,
    `Weight profiles: ${rawWeightProfiles.length} (${rawWeightProfiles.filter(w => w.learnedModifier !== T.NEUTRAL_MODIFIER).length} non-neutral)`,
    `Signal families with data: ${rawCalibProfiles.filter(c => c.confidenceBias !== 'insufficient_data').map(c => c.signalKey).join(', ') || 'none'}`,
    `Regime segments: ${regimeStats.length}`,
  ];

  // Shadow mode: zero all recommendations, preserve learning data
  const calibrationProfiles = mode === 'shadow'
    ? rawCalibProfiles.map(c => ({ ...c, suggestedAdjustment: null, confidenceBias: 'insufficient_data' as const }))
    : rawCalibProfiles;

  const weightProfiles = mode === 'shadow'
    ? rawWeightProfiles.map(w => ({ ...w, learnedModifier: T.NEUTRAL_MODIFIER, effectiveWeight: T.NEUTRAL_MODIFIER, appliedRule: 'SHADOW_MODE_NO_ADJUSTMENT' }))
    : rawWeightProfiles;

  return {
    symbol, evaluationCount: evaluations.length, mode,
    reliability, calibrationProfiles, weightProfiles, regimeStats, lessons, reasoning,
    meta: { sufficientData, computedFromCount: evaluations.length },
  };
}
